import './style.css';
import { AppState } from './core/AppState';
import { Sidebar } from './ui/components/Sidebar';
import { PreviewPane } from './ui/components/PreviewPane';
import { StructureTree } from './ui/components/StructureTree';
import { HexEditor } from './ui/components/HexEditor';

// Initialize App
const app = document.querySelector<HTMLDivElement>('#app')!;
app.className = 'app-container';

// State
const state = new AppState();

// Layout Structure
// Sidebar | Main
// Main = Preview (Top) | Editor (Bottom)
// Editor = Tree (Left) | Hex (Right)

// Create Containers
const sidebarContainer = document.createElement('div');
sidebarContainer.className = 'sidebar';
app.appendChild(sidebarContainer);

const mainArea = document.createElement('div');
mainArea.className = 'main-area';
mainArea.style.display = 'flex';
mainArea.style.flexDirection = 'column';
mainArea.style.flex = '1';
mainArea.style.overflow = 'hidden';
app.appendChild(mainArea);

// Header (Global for mobile mainly, but used as Preview Header on desktop)
// Effectively we are pulling it out of PreviewPane.
import { AppHeader } from './ui/components/AppHeader';

const headerContainer = document.createElement('header');
mainArea.appendChild(headerContainer); // Insert at top of main area matching previous visual order for Preview
// WAIT - On desktop, Sidebar is LEFT. Main is RIGHT.
// Header was inside Main -> PreviewPane.
// We put it in MainArea, so it stays on right. Correct.

new AppHeader(headerContainer, state);


const previewContainer = document.createElement('div');
// PreviewPane manages its own styles mostly, but we set container layout props
mainArea.appendChild(previewContainer);

const editorContainer = document.createElement('div');
editorContainer.className = 'editor-pane';
editorContainer.style.height = '45%';
editorContainer.style.backgroundColor = '#181818';
editorContainer.style.display = 'flex';
// editorContainer.style.borderTop = '1px solid var(--border)'; // Resizer handles border

// Resizer 2: Vertical (Preview vs Editor)
// We want to resize Editor height. Invert=true because handle moves UP to increase Size
import { Resizer } from './ui/Resizer';
const vResizer = new Resizer(previewContainer, editorContainer, 'vertical', true);
mainArea.appendChild(vResizer.handle);
mainArea.appendChild(editorContainer);

const treeContainer = document.createElement('div');
treeContainer.className = 'structure-tree';
treeContainer.style.width = '250px';
treeContainer.style.borderRight = '1px solid var(--border)';
treeContainer.style.display = 'flex';
treeContainer.style.flexDirection = 'column';
treeContainer.style.background = '#1a1a1a';
editorContainer.appendChild(treeContainer);

// Resizer 3: Horizontal (Tree vs Hex)
const treeResizer = new Resizer(treeContainer, undefined, 'horizontal');
editorContainer.appendChild(treeResizer.handle);

const hexContainer = document.createElement('div');
hexContainer.className = 'hex-editor';
hexContainer.style.flex = '1';
hexContainer.style.display = 'flex';
hexContainer.style.flexDirection = 'column';
hexContainer.style.background = '#121212';
hexContainer.style.overflow = 'hidden';
editorContainer.appendChild(hexContainer);

// Resizer 1: Sidebar
const sidebarResizer = new Resizer(sidebarContainer, undefined, 'horizontal');
// Insert after sidebarContainer
app.insertBefore(sidebarResizer.handle, mainArea);

// Instantiate Components
// Instantiate Components
new Sidebar(sidebarContainer, state);
new PreviewPane(previewContainer, state);
new StructureTree(treeContainer, state);
new HexEditor(hexContainer, state);

// Mobile Tab Bar Implementation
import { MobileTabBar, type MobileTab } from './ui/components/MobileTabBar';

const mobileTabContainer = document.createElement('div');
app.appendChild(mobileTabContainer);
const mobileTabBar = new MobileTabBar(mobileTabContainer);

// Mobile View Logic
// Helper to toggle visibility
const setMobileVisibility = (el: HTMLElement, visible: boolean) => {
  if (visible) {
    el.classList.add('mobile-visible');
    el.classList.remove('mobile-hidden');
  } else {
    el.classList.add('mobile-hidden');
    el.classList.remove('mobile-visible');
  }
};

// Mobile View Logic
const updateMobileView = (tab: MobileTab) => {
  // 1. File Tab (Combined Preview + Sidebar)
  if (tab === 'file') {
    setMobileVisibility(sidebarContainer, true);
    setMobileVisibility(previewContainer, true);
    setMobileVisibility(editorContainer, false);
    setMobileVisibility(headerContainer, true); // Keep header visible

    // Cleanup editor children
    setMobileVisibility(treeContainer, false);
    setMobileVisibility(hexContainer, false);
  } else if (tab === 'editor') {
    setMobileVisibility(sidebarContainer, false);
    setMobileVisibility(previewContainer, false);
    setMobileVisibility(editorContainer, true);
    setMobileVisibility(headerContainer, true); // Still keep header visible!

    // Editor children handled by CSS layout in .editor-pane
    treeContainer.classList.remove('mobile-hidden');
    hexContainer.classList.remove('mobile-hidden');
  }
};

// Initial Sync
updateMobileView('file');
mobileTabBar.setActiveTab('file');

mobileTabBar.onTabChange((tab) => {
  // Reset transforms and transitions to prevent "black screen" or lingering swipe state
  setTransform(getGroup('file'), 0);
  setTransform(getGroup('editor'), 0);
  setTransition(getGroup('file'), 'none');
  setTransition(getGroup('editor'), 'none');

  updateMobileView(tab);
});

// Swipe Logic with Visual Feedback (1:1 Tracking)
let touchStartX = 0;
let touchStartY = 0;
let isSwiping = false;

// Element Groups
const getGroup = (tab: MobileTab): HTMLElement[] => {
  if (tab === 'file') return [sidebarContainer, previewContainer, headerContainer];
  if (tab === 'editor') return [editorContainer, headerContainer];
  return [];
};

// Helper: Set Transform
const setTransform = (els: HTMLElement[], x: number) => {
  els.forEach(el => {
    // Header should NOT move if it is sticky (optional)
    // But if we want 1:1 slide of the whole view, header should slide too?
    // Or if header is global 'App Bar', it stays put?
    // User asked for "Maintain Undo/Redo header in Edit view".
    // If it stays put, that's better.
    if (el === headerContainer) return;

    el.style.transform = `translateX(${x}px)`;
  });
};

// Helper: Set Transition
const setTransition = (els: HTMLElement[], duration: string) => {
  els.forEach(el => {
    if (el === headerContainer) return;
    el.style.transition = duration;
  });
};

// Ensure visibility for animation
const ensureVisible = (els: HTMLElement[]) => {
  els.forEach(el => {
    el.classList.remove('mobile-hidden');
    el.classList.add('mobile-visible');
  });
};

document.addEventListener('touchstart', (e) => {
  // Only enable on mobile (check if tab bar is visible or width)
  if (window.innerWidth > 768) return;

  // Don't swipe if touching header controls?
  // Simple check Y coordinate?
  // Header is approx 60px height.
  // But header might be at top of sidebar in 'File' view?
  // Wait, on 'File' view, layout is: Top 40% (Header+Preview), Bottom 60% Sidebar.
  // Wait, Header was PART of PreviewPane.
  // Now Header is separate.

  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
  isSwiping = false;

  // Disable transitions for instant drag
  setTransition(getGroup('file'), 'none');
  setTransition(getGroup('editor'), 'none');
}, { passive: true });

document.addEventListener('touchmove', (e) => {
  if (window.innerWidth > 768) return;

  const touchCurrentX = e.touches[0].clientX;
  const touchCurrentY = e.touches[0].clientY;
  const diffX = touchCurrentX - touchStartX;
  const diffY = touchCurrentY - touchStartY;

  // Detect horizontal dominance
  if (!isSwiping) {
    if (Math.abs(diffX) > 10 && Math.abs(diffX) > Math.abs(diffY)) {
      isSwiping = true;
    } else {
      return; // Vertical scroll or noise
    }
  }

  // We are swiping
  const currentTab = mobileTabBar.currentTab;

  // Logic:
  // If File: Dragging Left (diffX < 0) pulls Editor from Right.
  // If Editor: Dragging Right (diffX > 0) pulls File from Left.

  if (currentTab === 'file') {
    if (diffX < 0) {
      // Pulling Editor in
      ensureVisible(getGroup('editor'));
      setTransform(getGroup('file'), diffX); // Move File Left
      setTransform(getGroup('editor'), window.innerWidth + diffX); // Editor starts at width, moves left
    }
  } else if (currentTab === 'editor') {
    if (diffX > 0) {
      // Pulling File in
      ensureVisible(getGroup('file'));
      setTransform(getGroup('editor'), diffX); // Move Editor Right
      setTransform(getGroup('file'), -window.innerWidth + diffX); // File starts at -width, moves right
    }
  }
}, { passive: true });

document.addEventListener('touchend', (e) => {
  if (!isSwiping) return;
  isSwiping = false;

  const touchEndX = e.changedTouches[0].clientX;
  const diffX = touchEndX - touchStartX;
  const threshold = window.innerWidth / 3;
  const currentTab = mobileTabBar.currentTab;

  // Enable transition for snap
  setTransition(getGroup('file'), 'transform 0.3s ease-out');
  setTransition(getGroup('editor'), 'transform 0.3s ease-out');

  // Determine outcome
  if (currentTab === 'file') {
    if (diffX < -threshold) {
      // Success: Switch to Editor
      setTransform(getGroup('file'), -window.innerWidth);
      setTransform(getGroup('editor'), 0);
      setTimeout(() => mobileTabBar.setActiveTab('editor'), 300);
    } else {
      // Revert
      setTransform(getGroup('file'), 0);
      setTransform(getGroup('editor'), window.innerWidth);
      // Hide neighbor after transition
      setTimeout(() => updateMobileView('file'), 300); // Use updateMobileView to hide editorContainer
    }
  } else if (currentTab === 'editor') {
    if (diffX > threshold) {
      // Success: Switch to File
      setTransform(getGroup('editor'), window.innerWidth);
      setTransform(getGroup('file'), 0);
      setTimeout(() => mobileTabBar.setActiveTab('file'), 300);
    } else {
      // Revert
      setTransform(getGroup('editor'), 0);
      setTransform(getGroup('file'), -window.innerWidth);
      setTimeout(() => {
        setMobileVisibility(sidebarContainer, false);
        setMobileVisibility(previewContainer, false);
        // And header logic? Header is constant now.
      }, 300);
    }
  }
});

// Keyboard Shortcuts
document.addEventListener('keydown', (e) => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
    e.preventDefault();
    if (e.shiftKey) state.redo();
    else state.undo();
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
    e.preventDefault();
    state.redo();
  }
});
